import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ClientByContact,
  MatchType,
  SearchCriteria,
  SupportRequestStatus,
  InstanceSupportRequestDetails,
  DivisionByClientContact,
  AuthPreferences,
} from '@models/dashboard/model';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs';
import { CurrentUserQuery } from 'src/app/state';
import { CommonConstants } from '../constants/common.constant';
import { CoreService } from '../models/clux/enum';
import { Uri } from '../utils/uri';
import { UtilityService } from './utility.service';
import { BrandService } from './brand.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private currentUserQuery = inject(CurrentUserQuery);
  private http = inject(HttpClient);
  private readonly brandService = inject(BrandService);
  private readonly utilityService = inject(UtilityService);

  /**
   * Retrieve basic client info associated with the specified contact email and store in Akita.
   * This function takes no action if the data already exists in Akita.
   */
  public loadClientsByContact(): Observable<ClientByContact[]> {
    const email = this.currentUserQuery.getEmailAddress();
    const tpaPartner = this.brandService.getTpaPartner();

    if (!email) {
      throw new Error('contact email not found');
    }

    if (!tpaPartner) {
      throw new Error('could not determine tpa');
    }

    const criteria: SearchCriteria = [
      {
        key: 'contactEmail',
        value: email,
        matchType: MatchType.EXACT,
      },
      {
        key: 'tpaPartner',
        value: tpaPartner,
        matchType: MatchType.EXACT,
      },
    ];

    const uri = new Uri('/profile/*/clientsByContact/search', CoreService.Dashboard);
    return this.http.post<ClientByContact[]>(uri.toString(), criteria).pipe(
      catchError((err: unknown) => {
        this.utilityService.showGrowelMessage(CommonConstants.errorCodes.ErrorFromApi, false, false);
        return throwError(() => err);
      })
    );
  }

  /**
   * Retrieve basic client info associated with the specified contact email and store in Akita.
   * This function takes no action if the data already exists in Akita.
   */
  public loadDivisionsByClientContact(clientId: string): Observable<DivisionByClientContact[]> {
    const email = this.currentUserQuery.getEmailAddress();

    if (!email) {
      throw new Error('contact email not found');
    }

    if (!clientId) {
      throw new Error('could not determine client ID');
    }

    const criteria: SearchCriteria = [
      {
        key: 'contactEmail',
        value: email,
        matchType: MatchType.EXACT,
      },
      {
        key: 'clientId',
        value: clientId,
        matchType: MatchType.EXACT,
      },
    ];

    const uri = new Uri('/profile/*/divisionsByClientContact/search', CoreService.Dashboard);
    return this.http.post<DivisionByClientContact[]>(uri.toString(), criteria).pipe(
      catchError((err: unknown) => {
        this.utilityService.showGrowelMessage(CommonConstants.errorCodes.ErrorFromApi, false, false);
        return throwError(() => err);
      })
    );
  }

  /**
   * Dashboard query to get the disableSupportRequest value from the Instance table to determine if support request creation functionality is available
   * @returns An observable emitting the boolean value of disableSupportRequest from the Instance
   */
  public getSupportRequestDetails(): Observable<InstanceSupportRequestDetails['clux']> {
    const query = {
      take: 1,
      orderDirection: 'desc',
      orderBy: 'updated',
    };

    const url = new Uri('/profile/*/supportRequestStatus/search', CoreService.Dashboard, query);
    return this.http.post<SupportRequestStatus[]>(url.toString(), []).pipe(
      catchError((err: unknown) => {
        this.utilityService.showGrowelMessage(CommonConstants.errorCodes.ErrorFromApi, false, false);
        return throwError(() => err);
      }),
      map((res) => res[0].supportRequestDetails.clux)
    );
  }

  public getClientMfaRequirements(clientId: string) {
    const url = new Uri(`/profile/${clientId}/clientMfaRequirements/search`, CoreService.Dashboard);

    return this.http.post<AuthPreferences[]>(String(url), []).pipe(
      map((response) => response[0]),
      catchError((err: unknown) => {
        this.utilityService.showGrowelMessage(CommonConstants.errorCodes.ErrorFromApi, false, false);
        return throwError(() => err);
      })
    );
  }
}
